import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  computed,
  effect,
  EventEmitter,
  inject,
  signal,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl, Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { of, switchMap } from 'rxjs';
import { format, isValid, parseISO } from 'date-fns';
import type { PositionConfig } from 'devextreme/common/core/animation';
import { ToastrService } from 'ngx-toastr';
import {
  ReportId,
  ReportIds,
  ReportInputParameterKeys,
  ReportingComparisonScopes,
  isKnownReportId,
  type AgencyBrandingOption,
  type AgencyBrandingOptionValue,
  type ReportingComparisonScope,
} from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import { PRODUCTION_DASHBOARD_CONTEXT } from '../../../modules/production-dashboards/production-dashboard-context';
import { AgencyBrandingApiService } from '../../../services/agency-branding-api.service';
import {
  ReportDataAvailabilityService,
  availabilityForReport,
} from '../../../services/report-data-availability.service';
import {
  ReportDefaultSettings,
  ReportDefaultSettingsKeys,
} from 'ag-common-lib/lib/models/reporting/report-default-settings.model';
import {
  ReportPeriodSelection,
  buildReportPeriodOptions,
  isPaidReport,
  isReportPeriod,
  reportRangeErrors,
  resolveReportPeriod,
  selectReportPeriod,
} from '../../../utils/report-period.utils';
import { ReportFilterOptionsService } from '../../../services/report-filter-options.service';
import {
  areAgencyBrandingOptionValuesEqual,
  DEFAULT_AGENCY_BRANDING_OPTION,
  getDefaultAgencyBrandingValue,
  shouldShowReportBranding,
  toAgencyBrandingOptionValue,
} from '../../../utils/agency-branding-options.utils';
import { resolveProductionReportDatasetType } from '../../../utils/production-report-default-date-range.utils';
import {
  getReportFilterPlaceholder,
  toReportFilters,
  type ReportFilterOption,
} from '../../../utils/report-filter-options.utils';
import { REPORTS_VIEWER_SECTIONS, REPORT_SCOPE_LABELS } from '../../../utils/report-sections.utils';
import {
  type ProductionReportInputVariables,
  type ProductionReportVariable,
} from '../reports-core/production-reports.models';
import { ProductionReportsService } from '../reports-core/production-reports.service';

import { getReportRealNamesWarning } from '../../../utils/report-real-names-warning.utils';

enum PreviewViewState {
  Loading = 'loading',
  Unavailable = 'unavailable',
  Error = 'error',
  Pdf = 'pdf',
  Empty = 'empty',
}

const PREVIEW_SHELL_SELECTOR = '.production-report-viewer__preview-shell';
/** Clears the browser PDF viewer's toolbar (about 56px) with a little room to spare. */
const SETTINGS_POPUP_TOP_OFFSET = 72;
const SETTINGS_POPUP_BOTTOM_GAP = 12;
const SETTINGS_POPUP_MIN_HEIGHT = 240;

@Component({
  selector: 'ag-shr-production-report-viewer',
  templateUrl: './production-report-view.component.html',
  styleUrls: ['./production-report-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ProductionReportsService],
  host: { class: 'production-report-viewer' },
  encapsulation: ViewEncapsulation.None,
})
export class ProductionReportViewerComponent implements OnInit, OnChanges, OnDestroy {
  /** ID of the report to load */
  @Input({ required: true }) set reportId(value: ReportId | string) {
    this.reportIdSig.set(value);
  }
  get reportId(): ReportId | string {
    return this.reportIdSig();
  }
  private readonly reportIdSig = signal<ReportId | string>('');

  /** Default start date (ISO string). When provided and no route start date supplied, used as initial value. */
  @Input() defaultStartDate: string | null = null;

  /** Default end date (ISO string). When provided and no route end date supplied, used as initial value. */
  @Input() defaultEndDate: string | null = null;

  /** Optional branding override */
  @Input() brandingId: AgencyBrandingOptionValue | null = null;

  /** The report section this page was opened from; sent with every request so the API limits the data to it. */
  @Input() set scope(value: ReportingComparisonScope | null | undefined) {
    this.scopeSig.set(value ?? ReportingComparisonScopes.allianceGroup);
  }

  /** When true, Apply also saves the current settings as the report's defaults for every user. */
  @Input() saveDefaultsOnApply = false;

  /** When true, the report starts from the saved default settings; otherwise from the built-in ones. */
  @Input() useStoredDefaults = false;

  /** Optional pre-set variable values */
  @Input() variables: Record<string, unknown> = {};

  /** Optional generatedAt date string (ISO) */
  @Input() generatedAt: string | null = null;

  /** Optional report title override for consumers that need project-specific labeling. */
  @Input() reportTitleOverride: string | null = null;

  /** Optional bulletin/subtitle override for consumers that need project-specific labeling. */
  @Input() bulletinTitleOverride: string | null = null;

  /** When true, Apply/Reset emits current variables so the route owns report reload state. */
  @Input() syncAppliedVariablesToRoute = false;

  @Output() readonly appliedVariablesChange = new EventEmitter<ProductionReportInputVariables>();
  @Output() readonly backToReportsList = new EventEmitter<void>();

  private readonly sanitizer = inject(DomSanitizer);
  private readonly titleService = inject(Title);
  private readonly route = inject(ActivatedRoute);
  private readonly productionReportsService = inject(ProductionReportsService);
  private readonly reportDataAvailability = inject(ReportDataAvailabilityService);
  protected readonly periodSelectionSig = signal<ReportPeriodSelection | null>(null);
  protected readonly dataAvailableThroughSig = signal<string | null>(null);
  protected readonly dataAvailableFromSig = signal<string | null>(null);
  protected readonly isResolvingPeriodSig = signal(false);
  protected readonly paidReportSig = computed(() => isPaidReport(this.reportIdSig()));
  protected readonly periodInvalidSig = computed(() => {
    const selection = this.periodSelectionSig();
    if (!selection) return false;
    if (reportRangeErrors(selection, this.dataAvailableThroughSig()).length) return true;
    return (
      selection.period !== 'custom' &&
      !buildReportPeriodOptions(
        selection.period,
        this.dataAvailableThroughSig(),
        undefined,
        this.dataAvailableFromSig(),
      ).some(option => option.value === selection.periodEnding)
    );
  });
  private readonly reportFilterOptionsService = inject(ReportFilterOptionsService);
  private readonly agencyBrandingApiService = inject(AgencyBrandingApiService);
  private readonly dashboardContext = inject(PRODUCTION_DASHBOARD_CONTEXT);

  private readonly toastrService = inject(ToastrService);
  private readonly document = inject(DOCUMENT);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly defaultDocumentTitle = this.titleService.getTitle();

  private activeRequestId = 0;
  private settingsPopupScheduleId = 0;
  private reportFilterReloadTimer: ReturnType<typeof setTimeout> | null = null;

  public readonly stateSig = this.productionReportsService.stateSig;
  public readonly selectedReportSig = this.productionReportsService.selectedReportSig;

  protected readonly errorSig = signal<string | null>(null);
  protected readonly pdfObjectUrlSig = signal<string | null>(null);
  protected readonly pdfPreviewUrlSig = signal<SafeResourceUrl | null>(null);
  protected readonly previewViewStates = PreviewViewState;
  protected readonly selectedReportFilterValuesSig = signal<string[]>([]);
  protected isSettingsPopupVisible = false;
  protected readonly isSettingsPopupCollapsedSig = signal(false);

  /**
   * The panel starts below the browser PDF viewer's own toolbar, whose download / print / more
   * controls it used to cover. It never slides back up over them: when the report area is short
   * the panel gets shorter and its content scrolls.
   */
  protected readonly settingsPopupPosition: PositionConfig = {
    my: { x: 'right', y: 'top' },
    at: { x: 'right', y: 'top' },
    of: PREVIEW_SHELL_SELECTOR,
    offset: { x: -12, y: SETTINGS_POPUP_TOP_OFFSET },
    collision: { x: 'fit', y: 'none' },
  };

  protected get settingsPopupMaxHeight(): number {
    const shell = document.querySelector(PREVIEW_SHELL_SELECTOR)?.getBoundingClientRect();
    const available = (shell?.height ?? window.innerHeight) - SETTINGS_POPUP_TOP_OFFSET - SETTINGS_POPUP_BOTTOM_GAP;
    const viewportAvailable =
      window.innerHeight - Math.max(shell?.top ?? 0, 0) - SETTINGS_POPUP_TOP_OFFSET - SETTINGS_POPUP_BOTTOM_GAP;
    return Math.min(Math.max(available, SETTINGS_POPUP_MIN_HEIGHT), Math.max(viewportAvailable, 120));
  }

  protected readonly scopeSig = signal<ReportingComparisonScope>(ReportingComparisonScopes.allianceGroup);
  protected readonly brandingOptionsSig = signal<AgencyBrandingOption[]>([]);
  protected readonly showBrandingSig = computed(() =>
    shouldShowReportBranding(this.dashboardContext.platform, this.scopeSig()),
  );

  /** Other agencies' names stay off screen on scoped pages; see ReportFilterOptionsService. */
  protected readonly reportFilterOptionsSig = toSignal(
    toObservable(
      computed(() => ({
        scope: this.scopeSig(),
        reportId: this.reportIdSig(),
        agent: this.dashboardContext.viewedAgentSig(),
      })),
    ).pipe(
      // The agent is only a trigger: the API reads the viewed agent from the request.
      switchMap(({ scope, reportId }) =>
        isKnownReportId(reportId) ? this.reportFilterOptionsService.optionsFor(scope, reportId) : of([]),
      ),
    ),
    { initialValue: [] as ReportFilterOption[] },
  );

  protected readonly reportFilterPlaceholderSig = computed(() => getReportFilterPlaceholder(this.scopeSig()));

  protected readonly realNamesWarningSig = computed(() => {
    const variables = this.stateSig().variables;
    return getReportRealNamesWarning(
      this.reportIdSig(),
      variables[ReportInputParameterKeys.ntpDisplayMode],
      variables[ReportInputParameterKeys.showSuppressedAgents],
    );
  });

  protected readonly pdfBlobSig = computed(() => this.stateSig().pdfBlob);

  /** e.g. "Submitted – MGA 2026-09-22 - 2026-09-28", so a saved file says what it is. */
  protected readonly pdfFileNameSig = computed(() => {
    const variables = this.stateSig().variables;
    const period = [variables[ReportInputParameterKeys.startDate], variables[ReportInputParameterKeys.endDate]]
      .filter(value => value !== undefined && value !== null && String(value).trim().length > 0)
      .join(' - ');

    return [this.pageTitleSig() || 'Report', period].filter(Boolean).join(' ');
  });

  protected readonly templateSettingsSig = computed(() => {
    const state = this.stateSig();
    return state.templateDraft ?? this.selectedReportSig()?.template ?? null;
  });

  protected readonly settingsFormDataSig = computed<Record<string, unknown>>(() => {
    const templateSettings = this.templateSettingsSig();
    const selectedReport = this.selectedReportSig();
    const state = this.stateSig();

    const formData: Record<string, unknown> = {
      reportId: selectedReport?.id ?? '',
      title: templateSettings?.title ?? '',
      subtitle: templateSettings?.subtitle ?? '',
      brandingId: state.brandingId,
      rowsPerPage: templateSettings?.rowsPerPage ?? 1,
      rmdFilterIds: [],
      mgaFilterIds: [],
    };

    selectedReport?.variables.forEach(variable => {
      formData[`variable_${String(variable.name)}`] = this.getVariableInputValue(variable);
    });

    return formData;
  });

  protected readonly scopeLabelSig = computed(() => REPORT_SCOPE_LABELS[this.scopeSig()]);

  /** The tile's name in this report's section, e.g. "Paid – MGA", so the page reads like the tile it came from. */
  protected readonly pageTitleSig = computed(() => {
    const selectedReport = this.selectedReportSig();
    const tile = REPORTS_VIEWER_SECTIONS.find(section => section.scope === this.scopeSig())
      ?.groups.flatMap(group => group.tiles)
      .find(candidate => candidate.kind === 'report' && candidate.reportId === selectedReport?.id);

    return tile?.name ?? selectedReport?.name ?? '';
  });

  /**
   * Whether this report has loaded once. The header's controls wait for it, so a report the API
   * refuses never flashes them; after that they stay, so a refused filter can be undone.
   */
  protected readonly hasLoadedReportSig = signal(false);

  /** Set when the API refused the report; the page then shows only the reason and a way back. */
  protected readonly unavailableReasonSig = computed(() => this.stateSig().unavailableReason);

  protected readonly previewViewStateSig = computed<PreviewViewState>(() => {
    const state = this.stateSig();
    const hasPdfPreview = Boolean(this.pdfPreviewUrlSig());

    if (state.isLoading && !hasPdfPreview) return PreviewViewState.Loading;
    if (state.unavailableReason) return PreviewViewState.Unavailable;
    if (Boolean(state.errorMessage)) return PreviewViewState.Error;
    if (hasPdfPreview) return PreviewViewState.Pdf;
    return PreviewViewState.Empty;
  });

  constructor() {
    effect(
      onCleanup => {
        const pdfBlob = this.pdfBlobSig();
        const objectUrl = pdfBlob ? URL.createObjectURL(pdfBlob) : null;

        this.pdfObjectUrlSig.set(objectUrl);
        this.pdfPreviewUrlSig.set(objectUrl ? this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl) : null);

        onCleanup(() => {
          if (objectUrl) URL.revokeObjectURL(objectUrl);
        });
      },
      { allowSignalWrites: true },
    );

    effect(() => {
      // A route `title` is the tab label. This prefix is only for hosts that do not set one.
      if (this.route.snapshot.routeConfig?.title != null) {
        return;
      }

      const reportName = this.selectedReportSig()?.name?.trim();
      this.titleService.setTitle(
        reportName ? `${reportName} | ${this.defaultDocumentTitle}` : this.defaultDocumentTitle,
      );
    });
  }

  ngOnInit(): void {
    void this.load();
  }

  ngOnDestroy(): void {
    if (this.reportFilterReloadTimer) {
      clearTimeout(this.reportFilterReloadTimer);
    }
    if (this.route.snapshot.routeConfig?.title != null) {
      return;
    }

    this.titleService.setTitle(this.defaultDocumentTitle);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const shouldReload =
      changes['reportId'] ||
      changes['brandingId'] ||
      changes['variables'] ||
      changes['generatedAt'] ||
      changes['reportTitleOverride'] ||
      changes['bulletinTitleOverride'];

    if (shouldReload && !changes['reportId']?.firstChange) {
      void this.load();
    }
  }

  protected toggleSettingsPopupCollapse = (): void => {
    this.isSettingsPopupCollapsedSig.update(isCollapsed => !isCollapsed);
  };

  /** Same fullscreen as the Comparison and Production Tracking pages. */
  protected toggleFullScreen = async (container: HTMLDivElement): Promise<void> => {
    if (this.document.fullscreenElement) {
      await this.document.exitFullscreen();
      return;
    }

    this.document
      .querySelectorAll('.ag-full-screen-app')
      .forEach(element => element.classList.remove('ag-full-screen-app'));
    await this.document.documentElement.requestFullscreen();
    container.classList.add('ag-full-screen-app');
  };

  @HostListener('document:fullscreenchange')
  protected fullscreenChanged(): void {
    if (!this.document.fullscreenElement) {
      this.document
        .querySelectorAll('.ag-full-screen-app')
        .forEach(element => element.classList.remove('ag-full-screen-app'));
    }
  }

  @HostListener('document:keydown.escape')
  protected onEscKeydown(): void {
    this.isSettingsPopupVisible = false;
  }

  protected onReportFilterTagsChange(value: unknown): void {
    const nextValues = Array.isArray(value) ? value.map(item => String(item)) : [];
    const currentValues = this.selectedReportFilterValuesSig();
    if (this.haveSameFilterValues(currentValues, nextValues)) {
      return;
    }

    this.selectedReportFilterValuesSig.set(nextValues);
    this.scheduleReportFilterReload();
  }

  protected shouldShowReportFilter(): boolean {
    // An MGA page covers one agency, so there is nothing to narrow.
    return (
      !this.unavailableReasonSig() &&
      this.reportId !== ReportIds.rmdSubmit &&
      this.reportId !== ReportIds.rmdPaid &&
      this.scopeSig() !== ReportingComparisonScopes.mga
    );
  }

  protected onTemplateTextChange = (field: 'title' | 'subtitle', value: string): void => {
    this.productionReportsService.updateTemplateDraft({ [field]: value });
  };

  protected onRowsPerPageChange = (value: unknown): void => {
    const parsedValue = Number(value);
    const normalizedValue = Number.isFinite(parsedValue) && parsedValue > 0 ? Math.floor(parsedValue) : 1;
    this.productionReportsService.updateTemplateDraft({ rowsPerPage: normalizedValue });
  };

  protected onVariableChange = (variable: ProductionReportVariable, value: unknown): void => {
    this.productionReportsService.updateVariable(variable.name, this.normalizeVariableValue(variable, value));
  };

  protected onBrandingIdChange = (value: unknown): void => {
    const brandingId = toAgencyBrandingOptionValue(value);
    if (brandingId === null) {
      return;
    }
    this.productionReportsService.updateBrandingId(brandingId);
  };

  protected onApply = async (): Promise<void> => {
    if (this.isResolvingPeriodSig() || this.periodInvalidSig()) return;
    this.applyPeriodRange();
    if (this.saveDefaultsOnApply && isKnownReportId(this.reportId)) {
      const isSaved = await this.productionReportsService.saveCurrentSettingsAsDefaults(
        this.reportId,
        this.periodSelectionSig()?.period,
      );
      if (!isSaved) {
        this.toastrService.error('Unable to update report default settings. Please try again.');
        return;
      }
    }

    if (this.syncAppliedVariablesToRoute) {
      this.appliedVariablesChange.emit(this.getAppliedVariablesForRoute());
    }

    await this.productionReportsService.loadSelectedReport({
      scope: this.scopeSig(),
      ...toReportFilters(this.selectedReportFilterValuesSig()),
    });
  };

  protected onPeriodSelectionChange(selection: ReportPeriodSelection): void {
    this.periodSelectionSig.set(selection);
  }

  protected onResetToDefaults = async (): Promise<void> => {
    if (this.isResolvingPeriodSig()) return;
    const requestId = ++this.activeRequestId;
    if (this.reportFilterReloadTimer) {
      clearTimeout(this.reportFilterReloadTimer);
      this.reportFilterReloadTimer = null;
    }
    this.selectedReportFilterValuesSig.set([]);
    this.productionReportsService.resetSelectedReport();
    const saved = await this.applyStoredDefaults();
    if (requestId !== this.activeRequestId) return;
    await this.initializePeriod(saved, true, requestId);
    if (requestId !== this.activeRequestId) return;
    this.applyTemplateOverrides();
    this.applySelectedBranding(this.brandingOptionsSig(), false);
    if (this.periodInvalidSig()) {
      this.productionReportsService.finishReportSetup();
      return;
    }
    this.applyPeriodRange();
    if (this.syncAppliedVariablesToRoute) this.appliedVariablesChange.emit(this.getAppliedVariablesForRoute());
    await this.productionReportsService.loadSelectedReport({ scope: this.scopeSig() });
  };

  private async load(): Promise<void> {
    this.activeRequestId += 1;
    const requestId = this.activeRequestId;
    if (this.reportFilterReloadTimer) {
      clearTimeout(this.reportFilterReloadTimer);
      this.reportFilterReloadTimer = null;
    }
    this.selectedReportFilterValuesSig.set([]);

    if (!isKnownReportId(this.reportId)) {
      this.errorSig.set(`Unknown report id: ${this.reportId || '-'}`);
      return;
    }

    this.errorSig.set(null);
    this.productionReportsService.selectReport(this.reportId);

    const selectedReport = this.selectedReportSig();
    if (!selectedReport) {
      this.errorSig.set(`Report is not configured: ${this.reportId}`);
      return;
    }

    this.settingsPopupScheduleId += 1;
    this.isSettingsPopupVisible = false;
    this.hasLoadedReportSig.set(false);
    const saved = await this.applyStoredDefaults();
    if (requestId !== this.activeRequestId) return;
    await this.initializePeriod(saved, false, requestId);
    if (requestId !== this.activeRequestId) return;
    this.applyVariables(selectedReport.variables, this.variables);
    this.applyTemplateOverrides();
    this.applyPeriodRange();
    await this.applyBrandingId();
    if (requestId !== this.activeRequestId) return;
    if (this.periodInvalidSig()) {
      this.productionReportsService.finishReportSetup();
      this.scheduleSettingsPopupOpen();
      return;
    }
    if (this.syncAppliedVariablesToRoute) this.appliedVariablesChange.emit(this.getAppliedVariablesForRoute());
    await this.productionReportsService.loadSelectedReport({ scope: this.scopeSig() });

    if (requestId !== this.activeRequestId) return;

    // Settings and controls appear once the report is known to be viewable, so a refused one never flashes them.
    if (!this.unavailableReasonSig()) {
      this.hasLoadedReportSig.set(true);
      this.scheduleSettingsPopupOpen();
    }

    if (this.generatedAt) {
      this.productionReportsService.setGeneratedDate(this.generatedAt);
    }
  }

  private scheduleSettingsPopupOpen(): void {
    const scheduleId = ++this.settingsPopupScheduleId;
    this.isSettingsPopupVisible = false;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (scheduleId !== this.settingsPopupScheduleId) return;
        this.isSettingsPopupVisible = true;
        // A plain field set outside any event: OnPush needs telling, now that this runs after the load.
        this.cdr.markForCheck();
      });
    });
  }

  private applyVariables(
    variableDefinitions: ProductionReportVariable[],
    routeVariables: Record<string, unknown>,
  ): void {
    variableDefinitions.forEach(variable => {
      if (!(variable.name in routeVariables)) return;
      this.productionReportsService.updateVariable(
        variable.name,
        this.normalizeVariableValue(variable, routeVariables[variable.name]),
      );
    });
  }

  private applyTemplateOverrides(): void {
    const patch: { title?: string; subtitle?: string } = {};
    if (this.bulletinTitleOverride) patch.title = this.bulletinTitleOverride;
    if (this.reportTitleOverride) patch.subtitle = this.reportTitleOverride;

    if (Object.keys(patch).length === 0) {
      return;
    }

    this.productionReportsService.updateTemplateDraft(patch);
  }

  private async initializePeriod(
    saved: ReportDefaultSettings | null,
    reset: boolean,
    requestId: number,
  ): Promise<void> {
    if (!resolveProductionReportDatasetType(this.reportId)) {
      this.periodSelectionSig.set(null);
      return;
    }
    this.isResolvingPeriodSig.set(true);
    this.dataAvailableThroughSig.set(null);
    this.periodSelectionSig.set(selectReportPeriod(this.paidReportSig(), 'custom'));
    const data = await this.reportDataAvailability.resolve();
    if (requestId !== this.activeRequestId) return;
    const { through, from } = availabilityForReport(data, this.reportId);
    this.dataAvailableThroughSig.set(through);
    this.dataAvailableFromSig.set(from);
    const route = reset ? {} : this.variables;
    const hasExplicitPeriod = isReportPeriod(route['period']);
    const hasExplicitDates =
      !reset &&
      (this.hasVariableValue(route, ReportInputParameterKeys.startDate) ||
        this.hasVariableValue(route, ReportInputParameterKeys.endDate) ||
        !!this.defaultStartDate ||
        !!this.defaultEndDate);
    const savedPeriod = saved?.[ReportDefaultSettingsKeys.period];
    const input =
      hasExplicitPeriod || hasExplicitDates
        ? {
            period: route['period'],
            periodEnding: route['period-ending'],
            startDate: route[ReportInputParameterKeys.startDate] ?? this.defaultStartDate,
            endDate: route[ReportInputParameterKeys.endDate] ?? this.defaultEndDate,
          }
        : {
            period: savedPeriod,
            startDate: savedPeriod === 'custom' ? saved?.[ReportDefaultSettingsKeys.startDate] : undefined,
            endDate: savedPeriod === 'custom' ? saved?.[ReportDefaultSettingsKeys.endDate] : undefined,
          };
    const selection = resolveReportPeriod(this.paidReportSig(), through, input, from);
    this.periodSelectionSig.set(
      reset && selection.period === 'custom' && reportRangeErrors(selection, through).length
        ? selectReportPeriod(this.paidReportSig(), 'custom')
        : selection,
    );
    this.isResolvingPeriodSig.set(false);
  }

  private applyPeriodRange(): void {
    const selection = this.periodSelectionSig();
    if (!selection) return;
    this.productionReportsService.updateVariable(ReportInputParameterKeys.startDate, selection.startDate);
    this.productionReportsService.updateVariable(ReportInputParameterKeys.endDate, selection.endDate);
  }

  private async applyStoredDefaults(): Promise<ReportDefaultSettings | null> {
    if (!this.useStoredDefaults) return null;
    return this.productionReportsService.applySavedDefaultSettings(this.reportId as ReportId);
  }

  private async applyBrandingId(): Promise<void> {
    const options = await this.loadBrandingOptions();
    this.brandingOptionsSig.set(options);
    this.applySelectedBranding(options, true);
  }

  private applySelectedBranding(options: AgencyBrandingOption[], preferInputBranding: boolean): void {
    const inputBrandingId = toAgencyBrandingOptionValue(this.brandingId);
    if (
      preferInputBranding &&
      inputBrandingId !== null &&
      options.some(option => areAgencyBrandingOptionValuesEqual(option.value, inputBrandingId))
    ) {
      this.productionReportsService.updateBrandingId(inputBrandingId);
      return;
    }

    const defaultValue = getDefaultAgencyBrandingValue(options);
    if (defaultValue === null) {
      return;
    }

    this.productionReportsService.updateBrandingId(defaultValue);
  }

  private async loadBrandingOptions(): Promise<AgencyBrandingOption[]> {
    if (!this.showBrandingSig()) {
      return [];
    }

    try {
      const response = await this.agencyBrandingApiService.getOptionsList({
        skip: 0,
        requireTotalCount: false,
        scope: this.scopeSig(),
      });

      const options = (response.data ?? []).flatMap(option => {
        const value = toAgencyBrandingOptionValue(option.value);
        if (value === null) {
          return [];
        }

        return [{ description: option.description, value }];
      });

      return options.length ? options : [DEFAULT_AGENCY_BRANDING_OPTION];
    } catch {
      // Branding is required to run the report, so a failed list must not leave it empty:
      // the default theme is always available.
      return [DEFAULT_AGENCY_BRANDING_OPTION];
    }
  }

  private hasVariableValue(vars: Record<string, unknown>, key: ReportInputParameterKeys): boolean {
    if (!(key in vars)) return false;
    return String(vars[key] ?? '').trim().length > 0;
  }

  private getAppliedVariablesForRoute(): ProductionReportInputVariables & {
    period?: string;
    'period-ending'?: string;
  } {
    const variables = this.stateSig().variables;

    const result = Object.values(ReportInputParameterKeys).reduce<ProductionReportInputVariables>(
      (accumulator, key) => {
        const value = variables[key];
        if (value !== undefined && value !== null && String(value).trim().length > 0) {
          accumulator[key] = value;
        }

        return accumulator;
      },
      {},
    );
    const selection = this.periodSelectionSig();
    return { ...result, ...(selection ? { period: selection.period, 'period-ending': selection.periodEnding } : {}) };
  }

  private scheduleReportFilterReload(): void {
    if (this.reportFilterReloadTimer) {
      clearTimeout(this.reportFilterReloadTimer);
    }

    this.reportFilterReloadTimer = setTimeout(() => {
      this.reportFilterReloadTimer = null;
      if (this.isResolvingPeriodSig() || this.periodInvalidSig()) return;
      void this.productionReportsService.loadSelectedReport({
        scope: this.scopeSig(),
        ...toReportFilters(this.selectedReportFilterValuesSig()),
      });
    }, 1000);
  }

  private haveSameFilterValues(currentValues: string[], nextValues: string[]): boolean {
    if (currentValues.length !== nextValues.length) {
      return false;
    }

    const currentValueSet = new Set(currentValues);
    return nextValues.every(value => currentValueSet.has(value));
  }

  private getVariableInputValue = (variable: ProductionReportVariable): string | number | boolean | null => {
    const value = this.stateSig().variables?.[variable.name];
    if (value === undefined || value === null) return '';

    if (variable.type === 'number') {
      if (value === '') return null;
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : null;
    }

    if (variable.type === 'boolean') {
      return value === true || value === 'true';
    }

    return String(value);
  };

  private normalizeVariableValue(variable: ProductionReportVariable, value: unknown): string | number | boolean {
    if (variable.type === 'date') return this.toDateInputValue(value);

    if (variable.type === 'number') {
      if (value === undefined || value === null || value === '') return '';
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : '';
    }

    if (variable.type === 'boolean') return value === true || value === 'true';

    if (value === undefined || value === null) return '';
    return String(value);
  }

  private toDateInputValue(value: unknown): string {
    if (value === undefined || value === null || value === '') return '';
    const date = value instanceof Date ? value : parseISO(String(value));
    if (!isValid(date)) return '';
    return format(date, 'yyyy-MM-dd');
  }
}
